import { useCapabilityRegistryContext } from '@mobile-mapping/providers';
import type { ControlMenuItemType } from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { LineDetectionCapability } from '@trimble/web3d-capabilities';
import { Accessor, batch, createMemo, createSignal } from 'solid-js';
import { CadEditingCapability } from '../feature-extraction/CadEditingCapability';

export interface UseMainToolbarSemiAutomaticDetectionResult {
  readonly semiAutomaticDetectionMenuItems: Accessor<ControlMenuItemType[]>;
  readonly isAnySemiAutomaticDetectionActive: Accessor<boolean>;
  deactivateAllSemiAutomaticDetection(): void;
}

/**
 * @intent Owns main-toolbar semi-automatic detection dropdown state.
 * Line detection is the first tool; more detection tools can join this menu later.
 */
export const useMainToolbarSemiAutomaticDetection =
  (): UseMainToolbarSemiAutomaticDetectionResult => {
    const { capabilityRegistry } = useCapabilityRegistryContext();
    const lineDetectionCapability = capabilityRegistry.get(
      LineDetectionCapability
    );
    const [selectedItemId, setSelectedItemId] = createSignal<
      string | undefined
    >();

    const isAnySemiAutomaticDetectionActive = createMemo(
      (): boolean => lineDetectionCapability()?.isActive() ?? false
    );

    const activeItemId = createMemo((): string | undefined =>
      isAnySemiAutomaticDetectionActive() ? selectedItemId() : undefined
    );

    const deactivateAllSemiAutomaticDetection = (): void =>
      batch(() => {
        void lineDetectionCapability()?.stop();
        setSelectedItemId(undefined);
      });

    const startTool = (itemId: string, start: () => void): void =>
      batch(() => {
        if (activeItemId() === itemId) {
          deactivateAllSemiAutomaticDetection();
          return;
        }
        for (const cadEditing of capabilityRegistry.getPaneCapabilities(
          CadEditingCapability
        )) {
          cadEditing.deactivate();
        }
        setSelectedItemId(itemId);
        start();
      });

    const semiAutomaticDetectionMenuItems = createMemo(
      (): ControlMenuItemType[] => {
        const lineDetection = lineDetectionCapability();
        if (!lineDetection) {
          return [];
        }

        return [
          {
            id: 'line-detection',
            type: 'action' as const,
            label: t('web3D.toolbar.actions.lineDetection'),
            icon: 'tc-icon-line',
            selected: activeItemId() === 'line-detection',
            onClick: () =>
              startTool('line-detection', () => {
                void lineDetection.start();
              }),
          },
        ];
      }
    );

    return {
      semiAutomaticDetectionMenuItems,
      isAnySemiAutomaticDetectionActive,
      deactivateAllSemiAutomaticDetection,
    };
  };
